import { useState } from 'react';
import {
    Drawer, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Toolbar
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import LanguageIcon from '@mui/icons-material/Language';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CampaignIcon from '@mui/icons-material/Campaign';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { Link, useLocation } from 'react-router-dom';

const drawerWidth = 240;

const Sidebar = ({ open }) => {
    const location = useLocation();

    const [openTrade, setOpenTrade] = useState(true);
    const [openExport, setOpenExport] = useState(false);

    const role = (sessionStorage.getItem('role') || '').toLowerCase();
    const isSuperAdmin = role === 'super_admin';

    const getItemProps = (path, padding = 4) => {
        const isExact = location.pathname === path;
        const isNested = path !== '/' && location.pathname.startsWith(`${path}/`);
        return {
            component: Link,
            to: path,
            selected: isExact || isNested,
            sx: {
                width: '100%',
                boxSizing: 'border-box',
                paddingLeft: `${padding * 6}px`,
                paddingRight: '16px',
                borderRadius: '6px',
                marginX: 1,
                marginY: 0.5,
                transition: 'all 0.2s ease-in-out',
                fontWeight: 500,
                color: '#2e1e14',
                '&.Mui-selected': {
                    backgroundColor: 'rgba(57, 58, 128, 0.15)',
                    color: '#1976d2',
                    fontWeight: 600,
                    '&:hover': {
                        backgroundColor: 'rgba(128, 80, 57, 0.25)',
                    },
                },
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
            }
        };
    };

const renderListItem = (path, label, padding = 4, icon = null) => {
    const itemProps = getItemProps(path, padding);
    const sx = {
        ...itemProps.sx,
        ...(icon ? { paddingLeft: '16px', gap: 1 } : {}),
    };
    return (
        <ListItemButton {...itemProps} sx={sx}>
            {icon && (
                <ListItemIcon
                    sx={{
                        minWidth: 32,
                        color: itemProps.selected ? '#1976d2' : '#5f6368',
                    }}
                >
                    {icon}
                </ListItemIcon>
            )}
            <ListItemText
                primary={label}
                primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#2e1e14',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                    lineHeight: 1.3
                }}
            />
        </ListItemButton>
    );
};

    const renderSubItem = (path, label, padding = 6) => renderListItem(path, label, padding, null);

    const isBoostSection = ['/boost-list', '/user-boost-list', '/tickets'].some((path) => location.pathname.startsWith(path));
    const boostToggleStyles = {
        width: '100%',
        boxSizing: 'border-box',
        paddingLeft: '16px',
        paddingRight: '16px',
        borderRadius: '6px',
        marginX: 1,
        marginY: 0.5,
        transition: 'all 0.2s ease-in-out',
        fontWeight: 500,
        color: '#2e1e14',
        display: 'flex',
        gap: 1,
        '&.Mui-selected': {
            backgroundColor: 'rgba(57, 58, 128, 0.15)',
            color: '#1976d2',
            fontWeight: 600,
            '&:hover': {
                backgroundColor: 'rgba(128, 80, 57, 0.25)',
            },
        },
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: open ? drawerWidth : 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                overflowX: 'hidden',
                transition: 'width 0.4s ease',
                '& .MuiDrawer-paper': {
                    width: open ? drawerWidth : 0,
                    overflowX: 'hidden',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    backgroundColor: '#fff',
                    borderRight: '1px solid #ddd',
                    color: '#2e1e14',
                    fontFamily: 'Segoe UI, Roboto, sans-serif',
                    transition: 'width 0.4s ease'
                }
            }}
        >
            <Toolbar />
            <List sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.3s ease' }}>
                <ListItemButton onClick={() => setOpenTrade(!openTrade)}>
                    <ListItemIcon sx={{ color: '#1976d2' }}>
                        <LanguageIcon />
                    </ListItemIcon>
                    {open && <ListItemText primary="Admin Paneli" />}
                    {open && (openTrade ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>

                <Collapse in={openTrade} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {isSuperAdmin && renderListItem('/admin-list', 'Admin Listesi', 4, <AdminPanelSettingsIcon fontSize="small" />)}
                        {renderListItem('/user-list', 'Kullanıcı Listesi', 4, <PeopleIcon fontSize="small" />)}
                        {renderListItem('/trending-pairs', 'Trendler', 4, <TrendingUpIcon fontSize="small" />)}
                        {renderListItem('/announcements', 'Duyurular', 4, <CampaignIcon fontSize="small" />)}
                        {renderListItem('/friend-referrals', 'Friend Referrals', 4, <GroupAddIcon fontSize="small" />)}
                        {renderListItem('/admin/offer-requests', 'Teklif Talepleri', 4, <RequestQuoteIcon fontSize="small" />)}

                        <ListItemButton
                            onClick={() => setOpenExport(!openExport)}
                            selected={isBoostSection}
                            sx={boostToggleStyles}
                        >
                            <ListItemIcon sx={{ minWidth: 32, color: isBoostSection ? '#1976d2' : '#5f6368' }}>
                                <RocketLaunchIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Boost Yönetimi" />
                            {openExport ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>

                        <Collapse in={openExport} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {renderSubItem('/boost-list', 'Boost Kuralları')}
                            </List>
                        </Collapse>
                        <Collapse in={openExport} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {renderSubItem('/user-boost-list', 'Kullanıcı Boost Listesi')}
                            </List>
                        </Collapse>
                        <Collapse in={openExport} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {renderSubItem('/tickets', 'Boost Ticket Listesi')}
                            </List>
                        </Collapse>
                        {renderListItem('/admin/feedback-tickets', 'Feedback Tickets', 4, <HeadsetMicIcon fontSize="small" />)}
                        {renderListItem('/how-to-use', 'Nasıl Kullanılır ?', 4, <HelpOutlineIcon fontSize="small" />)}
                    </List>
                </Collapse>
            </List>
        </Drawer>
    );
}
export default Sidebar;
